import React from 'react';
import { Box, Tooltip, TooltipProps, styled } from '@mui/material';
import { useTheme } from '../../theme/ThemeContext';

interface ThemeToggleProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const StyledTooltip = styled(({ className, ...props }: TooltipProps & { className?: string }) => (
  <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
  '&.theme-toggle-tooltip': {
    backgroundColor: theme.palette.grey[800],
    color: '#fff',
    fontSize: '12px',
    fontFamily: 'Open Sans',
    fontWeight: 500,
    padding: '6px 8px',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
}));

/**
 * Theme toggle component that allows users to switch between light and dark themes
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
  size = 'medium',
  className = ''
}) => {
  const { mode, toggleTheme } = useTheme();
  const isDark = mode === 'dark';

  return (
    <StyledTooltip
      title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
      className="theme-toggle-tooltip"
      placement="top"
      arrow
    >
      <Box
        onClick={toggleTheme}
        className={className}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleTheme();
          }
        }}
        aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          transition: 'opacity 0.3s ease',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        <Box
          component="img"
          src={isDark ? "/images/theme-dark.svg" : "/images/theme-light.svg"}
          alt={isDark ? "Dark mode" : "Light mode"}
          sx={{
            width: '64px',
            height: '36px',
          }}
        />
      </Box>
    </StyledTooltip>
  );
};

export default ThemeToggle;